import { useState, useEffect } from 'react';
import { subscribe } from '../../../../services/utils/pubsub';
import { ComponentEvents } from '../../../../services/wss/events/ComponentEvents';
import groupNodeEngine from '../Engines/GroupNodeEngine';
import topicGroupEngine from '../YourNotesGroupManager/TopicGroupEngine';
import { EngineNames } from '../../../../services/wss/events/EngineNames';

/**
 * useYourNotesNode
 *
 * Paint-trigger hook for YourNotesNode component.
 * Responsible for translating GroupNodeEngine state changes into React re-renders.
 *
 * This hook contains NO business logic. All decisions about which node is active,
 * which entries to load, and which entry is selected are made by GroupNodeEngine.
 *
 * How it works:
 * - Subscribes to ComponentNotifier (via pubsub events.onComponentEvent) on mount
 * - Filters on GroupNodeEngine/nodesChanged notifications
 * - Updates local React state to trigger a re-render with the latest engine state
 * - Exposes engine actions as pass-through calls so the component never imports the engine directly
 *
 * Relevance:
 * - Keeps YourNotesNode a pure rendering component
 * - Decouples React state management from engine business logic
 * - Single subscription point for all node-related UI updates
 */
export default function useYourNotesNode() {
    // Active node key — e.g. 'g1', 'g2'. Null until engine pushes first state.
    const [activeNode, setActiveNode] = useState(null);

    // Node descriptors — array of { key, label, entries, total } built by GroupNodeEngine.
    const [nodes, setNodes] = useState([]);

    // Currently selected entry — null if none selected.
    const [selectedEntry, setSelectedEntry] = useState(null);

    const [selectGroup, setSelectedGroup] = useState(null);

    // Handles engine notifications — updates React state to trigger re-render.
    // Ignores any event not from GroupNodeEngine/nodesChanged.
    const handleEngineEvent = (e) => {
        // if (e.name !== 'GroupNodeEngine' || e.target !== 'nodesChanged') {
        //     return;
        // }

        if (e.name === EngineNames.GroupNodeEngine && e.target === 'nodesChanged') {
            return _update(e);
        }

        // if (e.name === EngineNames.TopicGroupEngine && e.target === 'groupSaved') {
        //     const node = groupNodeEngine.getActiveNode();
        //     const items = groupNodeEngine.getNodes();
        //     const group = groupNodeEngine.getSelectedEntry();

        //     // if (activeNode !== null && activeNode !== node) {
        //     //     setActiveNode(node);
        //     // }

        //     // if (group !== null && group !== selectGroup) {
        //     //     setSelectedGroup(group);
        //     // }

        //     if (items?.length > 0 && _compareNodes(items)) {
        //         setSelectedEntry(group);
        //     }
        // }
    };

    // const _compareNodes = (items) => {
    //     if (!items) return false;

    //     // check if label has changed
    //     let changed = false;
    //     nodes.every((node) => {
    //         const found = items.find((x) => x.label === node.label);
    //         if (found) return true;

    //         changed = true;
    //         return false;
    //     });

    //     return changed;
    // };

    const _update = (e) => {
        setActiveNode(e.data.activeNode);
        setNodes(e.data.nodes);
        setSelectedEntry(e.data.selectedEntry);
        return 0;
    };

    // Subscribe to ComponentNotifier on mount, unsubscribe on unmount.
    // Ensures no stale subscriptions when component is destroyed.
    useEffect(() => {
        // ADDED — sync current engine state on mount in case engine already fired before component mounted
        setActiveNode(groupNodeEngine.getActiveNode());
        setNodes(groupNodeEngine.getNodes());
        setSelectedEntry(groupNodeEngine.getSelectedEntry());

        const unsub = subscribe(ComponentEvents.channel, handleEngineEvent);
        return () => unsub();
    }, []);

    return {
        // Current active node key — used to highlight selected node in header.
        activeNode,

        // All node descriptors for this group — used to render node list and pager.
        nodes,

        // Currently selected entry — used to highlight selected row.
        selectedEntry,

        // Pass-through actions — all business logic lives in GroupNodeEngine.

        // Switches the active node — triggers load if not cached.
        selectNode: (nodeKey) => groupNodeEngine.selectNode(nodeKey),

        // Sets the selected entry — notifies engine, triggers entrySelected event.
        selectEntry: (entry) => groupNodeEngine.selectEntry(entry),

        // Loads a specific page for a node — updates cache and triggers nodesChanged.
        loadPage: (nodeKey, page) => groupNodeEngine.loadPage(nodeKey, page),

        // Returns the current page number for a node — read from cache metadata.
        getCurrentPage: (nodeKey) => groupNodeEngine.getCurrentPage(nodeKey),
    };
}
